import type { StateCreator } from "zustand/vanilla";

import type { ItineraryDraft } from "../importItinerary";
import {
  type PickTarget,
  SUGGESTION_MAX_KEPT,
  type SuggestionCandidate,
  type SuggestionSession,
  type SuggestionStatus,
  type SuggestRouteType,
} from "../suggestItinerary";

/**
 * SuggestSessionSliceState is the open suggest shell slot.
 */
export interface SuggestSessionSliceState {
  /**
   * suggestSession is the open suggest shell state (null = the shell is closed).
   * It holds the chosen activity, the route type (loop vs. point-to-point), the
   * target distance/ascent/speed, the start + end map points, which endpoint a
   * map-pick is arming, the generated candidates, the seeds ever shown, the
   * previewed candidate, and the generation progress. Set only through the tool
   * bus, like every other UiAction, and
   * carries only plain serializable data — the same split the import session
   * makes. Mutually exclusive with the create/import/zone body slots by the
   * openContent flag, not by this field.
   */
  suggestSession: SuggestionSession | null;
}

/**
 * SuggestSessionSliceActions are the whole-session open/close plus the narrow
 * field setters that fill an open suggest session in, reached only through the
 * tool bus.
 */
export interface SuggestSessionSliceActions {
  /**
   * openSuggest opens the suggest shell, seeding a fresh session (no activity, a
   * loop route, a 10 km target distance, no ascent/speed/start/end override, no
   * candidates, not exhausted, an idle status). A second open replaces the prior
   * session, so re-opening starts clean. The narrower setSuggest* setters fill the
   * session in as the user picks inputs and generates candidates. `picking`
   * controls which endpoint pick the session opens armed on: it defaults to
   * "start" (a human entering the flow places the start with the next map click),
   * and a caller that supplies coordinates directly — the co-pilot's headless
   * session — passes null so the map cursor is never locked on a pick nobody
   * will click.
   */
  openSuggest: (options?: { picking?: PickTarget | null }) => void;
  /**
   * closeSuggest clears the suggest shell state (the slot becomes null). The whole
   * session — inputs, candidates, kept set, preview — is dropped; the open/close
   * tools drive this, the narrower setters update one field of an already-open
   * session.
   */
  closeSuggest: () => void;
  /**
   * setSuggestActivity sets the resolved activity id on the open suggest session.
   * A no-op when the shell is closed, mirroring the narrow import setters.
   */
  setSuggestActivity: (activityId: string | null) => void;
  /**
   * setSuggestRouteType switches the route shape (loop vs. point-to-point) on the
   * open suggest session. A no-op when the shell is closed.
   */
  setSuggestRouteType: (routeType: SuggestRouteType) => void;
  /**
   * setSuggestDistance sets the target distance in meters on the open suggest
   * session. A no-op when the shell is closed.
   */
  setSuggestDistance: (distanceMeters: number) => void;
  /**
   * setSuggestAscent sets the target ascent in meters (null = use the activity
   * default) on the open suggest session. A no-op when the shell is closed.
   */
  setSuggestAscent: (ascentMeters: number | null) => void;
  /**
   * setSuggestSpeed sets the speed override in km/h (null = use the activity
   * default) on the open suggest session. A no-op when the shell is closed.
   */
  setSuggestSpeed: (speedKmh: number | null) => void;
  /**
   * setSuggestStart sets the start map point ([lng, lat], null clears it) on the
   * open suggest session. A no-op when the shell is closed.
   */
  setSuggestStart: (start: [number, number] | null) => void;
  /**
   * setSuggestEnd sets the end map point ([lng, lat], null clears it) on the open
   * suggest session — used only for a point-to-point route. A no-op when the shell
   * is closed.
   */
  setSuggestEnd: (end: [number, number] | null) => void;
  /**
   * setSuggestAgentSelected records (or, with false, clears) that the co-pilot's
   * run deliberately selected a candidate since the last generation — the fact
   * the agent-origin startFrom gate reads. A no-op when the shell is closed.
   */
  setSuggestAgentSelected: (agentSelected: boolean) => void;
  /**
   * setSuggestTargetSegment records the existing segment this batch is meant to
   * replace, or null to mean "add a new one". A no-op when the shell is closed.
   */
  setSuggestTargetSegment: (targetSegmentId: string | null) => void;
  /**
   * setSuggestPicking arms (or, with null, disarms) a map-pick for the given
   * endpoint on the open suggest session. The map hook reads this to lock the
   * cursor and capture the next click. A no-op when the shell is closed.
   */
  setSuggestPicking: (picking: PickTarget | null) => void;
  /**
   * setSuggestionCandidates replaces the candidate list and the accumulated
   * seen-seed set on the open suggest session — the result of a generate or
   * regenerate — and records whether that batch came back short of the requested
   * count (`exhausted`, the pool is spent for the current constraints). `exhausted`
   * defaults false, so the clear-on-constraint-change path (which passes two empty
   * lists) also lifts a stale exhausted flag in the same write. A previewed
   * selection that the new batch no longer contains (a regenerate dropped it, or
   * the constraint-change path cleared the list) is reset to null in the same
   * write, so the preview never dangles on a candidate that is gone. A no-op when
   * the shell is closed.
   */
  setSuggestionCandidates: (
    candidates: SuggestionCandidate[],
    seenSeeds: number[],
    exhausted?: boolean,
  ) => void;
  /**
   * setSuggestionStatus sets the generation progress (idle / generating / ready /
   * error) on the open suggest session. A no-op when the shell is closed.
   */
  setSuggestionStatus: (status: SuggestionStatus) => void;
  /**
   * selectSuggestionCandidate sets which candidate the map preview draws by seed
   * (null clears the preview). A no-op when the shell is closed.
   */
  selectSuggestionCandidate: (seed: number | null) => void;
  /**
   * toggleSuggestionKeep flips the kept flag of one candidate matched by seed so a
   * regenerate reproduces it. A flip that would push the kept count past
   * SUGGESTION_MAX_KEPT is a no-op (the cap leaves fresh slots in the result); a
   * no-op too when the shell is closed or no candidate matches the seed.
   */
  toggleSuggestionKeep: (seed: number) => void;
  /**
   * setSuggestDraft replaces the editable itinerary draft on the open suggest
   * session — the working copy a chosen route is shaped into, edited, and
   * committed. A no-op when the shell is closed, mirroring setImportDraft.
   */
  setSuggestDraft: (draft: ItineraryDraft | null) => void;
  /**
   * setSuggestName sets the editable itinerary name the commit persists, on the
   * open suggest session. A no-op when the shell is closed, mirroring setImportName.
   */
  setSuggestName: (name: string) => void;
  /**
   * setSuggestOrganization sets the owning organization id on the open suggest
   * session (null lets the backend default to the authenticated user's org). A
   * no-op when the shell is closed, mirroring setImportOrganization.
   */
  setSuggestOrganization: (organizationId: string | null) => void;
  /**
   * setSuggestPrivate sets whether the commit persists the itinerary as private,
   * on the open suggest session. A no-op when the shell is closed, mirroring
   * setImportPrivate.
   */
  setSuggestPrivate: (isPrivate: boolean) => void;
  /**
   * setSuggestCollections sets the collections the committed itinerary will be
   * added to on the open suggest session. A no-op when the shell is closed,
   * mirroring setImportCollections.
   */
  setSuggestCollections: (collectionIds: string[]) => void;
}

/**
 * suggestSessionInitialState seeds the suggest shell closed.
 */
export const suggestSessionInitialState: SuggestSessionSliceState = {
  suggestSession: null,
};

/**
 * createSuggestSessionSlice builds the suggest shell actions. openSuggest seeds a
 * fresh idle session; every narrow setter no-ops while the shell is closed so a
 * late field write never resurrects a closed shell.
 */
export const createSuggestSessionSlice: StateCreator<
  SuggestSessionSliceState & SuggestSessionSliceActions,
  [],
  [],
  SuggestSessionSliceActions
> = (set) => ({
  closeSuggest: () => set({ suggestSession: null }),
  openSuggest: (options) =>
    set({
      suggestSession: {
        activityId: null,
        // No delegated pick until the co-pilot's run deliberately selects one;
        // the agent-origin startFrom gate reads this.
        agentSelected: false,
        ascentMeters: null,
        candidates: [],
        // Seed no collections; the shell's picker fills them in and the commit
        // attaches them after the create. Mirrors openImport.
        collectionIds: [],
        // Seed a 10 km target so the form opens on a sensible loop length the
        // user can tune before generating; the distance control edits it.
        distanceMeters: 10000,
        // No draft until a chosen route is shaped into one.
        draft: null,
        end: null,
        // Not exhausted until a generation comes back short of the target count.
        exhausted: false,
        // Seed private to match the itinerary CREATE flow's default; the shell's
        // privacy control flips it before the commit reads it. Mirrors openImport.
        isPrivate: true,
        name: "",
        // Seed null; the shell's OrganizationSelect writes the resolved default.
        // Null means the commit omits organization_id and the backend defaults to
        // the auth org. Mirrors openImport.
        organizationId: null,
        // Auto-arm the start-point pick on open (the default) so entering the
        // suggest flow locks the map cursor on the start point and the first click
        // sets it — the picking hook reads this slot; setSuggestStart clears it on
        // placement (Escape/cancelPick too). A caller that supplies coordinates
        // directly (the co-pilot's headless session) passes `picking: null` so the
        // cursor is never locked on a pick nobody will click.
        picking: options?.picking === undefined ? "start" : options.picking,
        routeType: "loop",
        seenSeeds: [],
        selectedSeed: null,
        speedKmh: null,
        start: null,
        status: { kind: "idle" },
        // Add a new segment unless a generate says otherwise; a fresh session
        // never inherits the previous batch's target.
        targetSegmentId: null,
      },
    }),

  selectSuggestionCandidate: (seed) =>
    set((state) =>
      state.suggestSession
        ? { suggestSession: { ...state.suggestSession, selectedSeed: seed } }
        : {},
    ),

  setSuggestActivity: (activityId) =>
    set((state) =>
      state.suggestSession ? { suggestSession: { ...state.suggestSession, activityId } } : {},
    ),

  setSuggestAgentSelected: (agentSelected) =>
    set((state) =>
      state.suggestSession ? { suggestSession: { ...state.suggestSession, agentSelected } } : {},
    ),

  setSuggestAscent: (ascentMeters) =>
    set((state) =>
      state.suggestSession ? { suggestSession: { ...state.suggestSession, ascentMeters } } : {},
    ),

  setSuggestCollections: (collectionIds) =>
    set((state) =>
      state.suggestSession ? { suggestSession: { ...state.suggestSession, collectionIds } } : {},
    ),

  setSuggestDistance: (distanceMeters) =>
    set((state) =>
      state.suggestSession ? { suggestSession: { ...state.suggestSession, distanceMeters } } : {},
    ),

  setSuggestDraft: (draft) =>
    set((state) =>
      state.suggestSession ? { suggestSession: { ...state.suggestSession, draft } } : {},
    ),

  setSuggestEnd: (end) =>
    set((state) =>
      state.suggestSession ? { suggestSession: { ...state.suggestSession, end } } : {},
    ),

  setSuggestionCandidates: (candidates, seenSeeds, exhausted = false) =>
    set((state) => {
      const session = state.suggestSession;
      if (!session) {
        return {};
      }

      // The preview selection only survives while its candidate is still in the
      // batch: a regenerate that does not reproduce the previewed seed (it was not
      // kept) drops it, and the clear-on-constraint-change path passes an empty
      // list — both must drop the now-dangling selection so the footer's "Start
      // from" CTA and the elevation preview never point at a candidate no longer
      // shown. A reproduced (kept) seed keeps its preview.
      const selectedSeed =
        session.selectedSeed !== null &&
        candidates.some((candidate) => candidate.seed === session.selectedSeed)
          ? session.selectedSeed
          : null;

      return {
        suggestSession: { ...session, candidates, exhausted, seenSeeds, selectedSeed },
      };
    }),

  setSuggestionStatus: (status) =>
    set((state) =>
      state.suggestSession ? { suggestSession: { ...state.suggestSession, status } } : {},
    ),

  setSuggestName: (name) =>
    set((state) =>
      state.suggestSession ? { suggestSession: { ...state.suggestSession, name } } : {},
    ),

  setSuggestOrganization: (organizationId) =>
    set((state) =>
      state.suggestSession ? { suggestSession: { ...state.suggestSession, organizationId } } : {},
    ),

  setSuggestPicking: (picking) =>
    set((state) =>
      state.suggestSession ? { suggestSession: { ...state.suggestSession, picking } } : {},
    ),

  setSuggestPrivate: (isPrivate) =>
    set((state) =>
      state.suggestSession ? { suggestSession: { ...state.suggestSession, isPrivate } } : {},
    ),

  setSuggestRouteType: (routeType) =>
    set((state) =>
      state.suggestSession ? { suggestSession: { ...state.suggestSession, routeType } } : {},
    ),

  setSuggestSpeed: (speedKmh) =>
    set((state) =>
      state.suggestSession ? { suggestSession: { ...state.suggestSession, speedKmh } } : {},
    ),

  setSuggestStart: (start) =>
    set((state) =>
      state.suggestSession ? { suggestSession: { ...state.suggestSession, start } } : {},
    ),

  setSuggestTargetSegment: (targetSegmentId) =>
    set((state) =>
      state.suggestSession ? { suggestSession: { ...state.suggestSession, targetSegmentId } } : {},
    ),

  toggleSuggestionKeep: (seed) =>
    set((state) => {
      const session = state.suggestSession;
      if (!session) {
        return {};
      }

      const target = session.candidates.find((candidate) => candidate.seed === seed);
      if (!target) {
        return {};
      }

      // A flip that would push the kept count past the cap is a no-op so a
      // regenerate always keeps fresh slots; un-keeping is always allowed.
      const keptCount = session.candidates.filter((candidate) => candidate.kept).length;
      if (!target.kept && keptCount >= SUGGESTION_MAX_KEPT) {
        return {};
      }

      return {
        suggestSession: {
          ...session,
          candidates: session.candidates.map((candidate) =>
            candidate.seed === seed ? { ...candidate, kept: !candidate.kept } : candidate,
          ),
        },
      };
    }),
});
